import { FiEdit } from '@react-icons/all-files/fi/FiEdit';
import { FiTrash2 } from '@react-icons/all-files/fi/FiTrash2';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Panel, ThError, ThLoading } from '@components/elements';
import QueryKeys from '@constants/queryKeys';
import * as api from '@company/services/worker/api';
import DATE_FORMATS from '@constants/dateFormat';
import IWorkerNotesData, {
  IWorkerNoteData,
} from '@company/types/workerNotes.type';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import IWorkerNoteCreation from '@company/types/workerNoteCreation.type';
import { IWorkerData } from '@th-types/worker.type';
import { useUserContext } from '@state/userContext';
import { useCompanyContext } from '@company/state/companyContext';
import JoinPremium from '@components/JoinPremium';
import ModalJoinPremium from '@company/components/Payment/ModalJoinPremium';
import useAlert from '@hooks/useAlert';

interface Params {
  handleNotesCount: (value: number) => void;
  worker: IWorkerData;
  isActive: boolean;
}

export default function WorkerNotes({
  handleNotesCount,
  worker,
  isActive,
}: Params) {
  const { id: userId } = useUserContext();
  const { premium: isPremiumCompany } = useCompanyContext();
  const { showSuccessAlert, showErrorAlert } = useAlert();
  const { register, reset, setValue, handleSubmit } =
    useForm<IWorkerNoteCreation>();

  const [noteEditing, setNoteEditing] = useState<IWorkerNoteData | undefined>();
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const { isLoading, error, data, refetch } = useQuery<
    IWorkerNotesData,
    AxiosError
  >({
    queryKey: [`${QueryKeys.WORKER_NOTES}_${worker.id}`],
    queryFn: () => api.fetchNotes(worker.id, 0, 100),
    enabled: isActive,
  });

  useEffect(() => {
    if (data && data.results) {
      handleNotesCount(data.results.length);
    }
  }, [data, handleNotesCount]);

  if (isLoading) return <ThLoading />;

  if (error) {
    if (error.response?.status) {
      return <ThError message="No data found" />;
    }
    return <ThError message={error.message} />;
  }

  // Show premium upgrade card if not premium
  if (!isPremiumCompany) {
    return (
      <>
        <div className="p-3">
          <JoinPremium
            title="Internal Notes"
            description="Keep private notes about workers that only your team can see. Internal notes help you track worker details, performance notes, and team communications privately. This feature is only available for premium members."
            joinText="JOIN PREMIUM"
            onJoinPremium={() => setShowPremiumModal(true)}
          />
        </div>

        {/* Premium Modal - Rendered outside container */}
        {showPremiumModal && (
          <ModalJoinPremium
            setShowModal={setShowPremiumModal}
            showMessage={(success, message) => {
              if (success) {
                showSuccessAlert(message);
              } else {
                showErrorAlert(message);
              }
            }}
          />
        )}
      </>
    );
  }

  const getStatus = (note: IWorkerNoteData) => {
    if (note.dateDeleted) {
      return 'Removed';
    }
    if (note.lastUpdated) {
      return 'Edited';
    }
    return '';
  };

  const onSubmit = async (dataRequest: IWorkerNoteCreation) => {
    if (noteEditing) {
      await api.updateNote(worker.id, noteEditing.id, dataRequest);
    } else {
      await api.createNote(worker.id, dataRequest);
    }

    setNoteEditing(undefined);
    refetch();
    reset();
  };

  const onDelete = async (workerNoteId: number) => {
    await api.deleteNote(worker.id, workerNoteId);
    refetch();
  };

  function onEdit(note: IWorkerNoteData): void {
    setValue('description', note.description);
    setNoteEditing(note);
  }

  const handleCancelEditing = () => {
    setNoteEditing(undefined);
    reset();
  };

  return (
    <>
      <div className="p-3">
        {/* Notes Section Header */}
        <Row className="mb-4">
          <Col>
            <p className="mb-2">
              <b>Notes</b>
            </p>
            <p className="mb-0 text-muted">
              These notes can only be seen by internal company members, at your
              company. The worker, or other companies, cannot see them. Still,
              please be professional.
            </p>
          </Col>
        </Row>

        {/* Existing Notes - Show first */}
        {data?.results && data.results.length > 0 && (
          <>
            <Row className="mb-4">
              {data.results.map((note) => (
                <Col md={6} sm={12} key={note.id} className="mb-3">
                  <Panel style={{ minHeight: 150 }}>
                    <div className="d-flex justify-content-between mb-3">
                      <p className="mb-0 fw-bold">
                        {`${note.createdBy.firstName} ${note.createdBy.lastName}`}
                      </p>
                      <p className="mb-0 fw-light text-muted">
                        {getStatus(note)}
                      </p>
                      <p className="mb-0 fw-bold">
                        {format(
                          new Date(note?.dateCreated),
                          DATE_FORMATS.DATE_FORMAT
                        )}
                      </p>
                    </div>
                    {!note.dateDeleted && (
                      <>
                        <p className="mb-3">{note.description}</p>
                        <div className="d-flex justify-content-end gap-4">
                          {userId === note.createdBy.id && (
                            <div
                              className="d-flex align-items-center gap-2 cursor-point fw-bold"
                              onClick={() => onEdit(note)}
                            >
                              <FiEdit size={14} />
                              EDIT NOTE
                            </div>
                          )}
                          <div
                            className="d-flex align-items-center gap-2 cursor-point fw-bold"
                            onClick={() => onDelete(note.id)}
                          >
                            <FiTrash2 size={14} />
                            DELETE NOTE
                          </div>
                        </div>
                      </>
                    )}
                  </Panel>
                </Col>
              ))}
            </Row>
            <hr className="my-4" />
          </>
        )}

        {/* Add New Note Form - At the bottom */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Control
                  as="textarea"
                  rows={3}
                  style={noteEditing && { border: '2px solid var(--yellow)' }}
                  {...register('description', { required: true })}
                  placeholder="Add a new internal note..."
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2">
                {noteEditing && (
                  <Button
                    variant="light"
                    className="fw-bold"
                    type="button"
                    onClick={handleCancelEditing}
                  >
                    CANCEL EDIT
                  </Button>
                )}
                <Button variant="primary" className="fw-bold" type="submit">
                  {noteEditing ? `EDIT NOTE` : 'POST NOTE'}
                </Button>
              </div>
            </Col>
          </Row>
        </form>
      </div>

      {/* Premium Modal - Rendered outside container */}
      {showPremiumModal && (
        <ModalJoinPremium
          setShowModal={setShowPremiumModal}
          showMessage={(success, message) => {
            if (success) {
              showSuccessAlert(message);
            } else {
              showErrorAlert(message);
            }
          }}
        />
      )}
    </>
  );
}
